import React, {useEffect} from "react";
import {useSelector} from "react-redux";
import BreadcrumbLayout from "@/components/common/CustomLayout/BreadcrumbLayout";
import './index.css'
import Button from "@/components/common/Button";
import {ReactComponent as RiAddLine} from "@/assets/icons/ri-add-line.svg";
import RefundTable from "@/pages/Refund/Table";
import RefundModal from "@/pages/Refund/Modal";
import {RootState, useAppDispatch} from "@/store";
import {getRefunds} from "@/store/refund-summary/action";
import useUserVenueSelectFilter from "@/hooks/useUserVenueSelectFilter";
import {dynamicAttr} from "@/utils/fallback";
import {getUserVenues} from "@/store/user-info/action";
import useSearchFilter from "@/hooks/useSearchFilter";
import useGenericSelectFilter from "@/hooks/useGenericSelectFilter";
import useCustomPaging from "@/hooks/useCustomPaging";
import GenericDatePicker from "@/components/common/GenericDatePicker";
import useDatePickerFilter from "@/hooks/useDatePickerFilter";
import {refundDetailActions} from "@/store/refund-detail/slice";
import {invoiceActions} from "@/store/invoice/slice";
import UserVenueSelect from "@/components/common/UserVenueSelect";
import GenericSelect from "@/components/common/GenericSelect";

const statusOptions = [
  'COMPLETED',
  'CUSTOMER_SUBMITTED',
  'CREATED'
]

const statusSelectOptions = statusOptions.map(s => ({
  value: s,
  label: s.replace('_', ' ')
}))

const Refund = () => {
  const dispatch = useAppDispatch()
  const [
    currentVenueId,
    selectedVenue,
    venueOptions,
    handleChangeVenue,
  ] = useUserVenueSelectFilter()
  const [
    searchString,
    currentSearchFilter,
    handleChangeSearchString,
    handleSubmitSearch
  ] = useSearchFilter()
  const [
    currentStatusFilter,
    selectedStatus,
    selectStatusOptions,
    handleChangeStatus,
  ] = useGenericSelectFilter('status', statusSelectOptions)
  const [
    createdDateTimestamp,
    currentSelectedDate,
    handleChangeDate
  ] = useDatePickerFilter('startDate')
  const [currentPageFilter] = useCustomPaging()
  const { show } = useSelector((rootState: RootState) => rootState.refundDetail.state.modal)
  
  const handleClickAdd = () => {
    dispatch(refundDetailActions.resetRefundDetail())
    dispatch(invoiceActions.resetInvoice())
    const param = {
      show: true,
      id: null
    } as RefundDetailModalPayload
    dispatch(refundDetailActions.showRefundDetailModal(param))
  }
  
  const getRefundsData = () => {
    const params = {
      size: 10,
      ...dynamicAttr('page', currentPageFilter),
      ...dynamicAttr('status', currentStatusFilter),
      ...dynamicAttr('venueId', currentVenueId),
      ...dynamicAttr('search', currentSearchFilter),
      ...dynamicAttr('startDate', createdDateTimestamp)
    }
    dispatch(getRefunds(params))
  }
  
  useEffect(() => {
    dispatch(getUserVenues({}))
    getRefundsData()
  }, [])
  
  useEffect(() => {
    getRefundsData()
  }, [
    currentPageFilter,
    currentStatusFilter,
    currentVenueId,
    currentSearchFilter,
    createdDateTimestamp
  ])
  
  return (
    <BreadcrumbLayout>
      <div className="flex w-full flex-col gap-7">
        <div className="flex w-full justify-between">
          <div className="flex gap-4">
            <div className="text-j2xl font-bold">
              Refund
            </div>
            <div className="separator"/>
            <UserVenueSelect
              name="venueId"
              id="venueId"
              placeholder="Select Venue"
              options={venueOptions}
              value={selectedVenue}
              onChange={handleChangeVenue}
            />
          </div>
          <div className="flex gap-4">
            <input
              value={searchString}
              onChange={handleChangeSearchString}
              onKeyDown={handleSubmitSearch}
              name="searchString"
              type="text"
              className="flex items-center py-[8px] px-4 gap-2 border rounded border-solid border-stroke-subtle text-jsm font-normal"
              placeholder="Search for Refunds"/>
            <Button
              onClick={handleClickAdd}
              type={null}
              variant="primary"
              className="whitespace-nowrap flex gap-2 pl-4 py-2 justify-center items-center">
              <div className="w-[18px]">
                <RiAddLine/>
              </div>
              <span className="text-jsm">Refund</span>
            </Button>
          </div>
        </div>
        <div className="card flex flex-col w-full">
          <div className="flex w-full justify-between py-4 px-6">
            <div className="flex gap-4 items-center">
              <div>
                <GenericSelect
                  name="status"
                  id="status"
                  placeholder="Select Status"
                  options={selectStatusOptions}
                  value={selectedStatus}
                  onChange={handleChangeStatus}
                />
              </div>
              <div className="separator"/>
              <div>
                <GenericDatePicker
                  selected={currentSelectedDate}
                  onChange={handleChangeDate}
                />
              </div>
            </div>
            <div className="flex justify-center items-center">
              10 per page
            </div>
          </div>
          <div>
            <RefundTable />
          </div>
        </div>
      </div>
      {
        show
          ? <RefundModal />
          : <></>
      }
    </BreadcrumbLayout>
  )
};

export default Refund;